import type { ExplorerTreeNode, ExplorerViewPoint, IfcMetaObject, RvtElement } from './types';

export function rvtElementName(element: RvtElement): string {
  return element.Name || `[${element.class ?? 'Element'} #${element.Id}]`;
}

export function ifcNodeName(metaObject: IfcMetaObject): string {
  return metaObject.name || metaObject.type || String(metaObject.id);
}

export function otherNodeName(metaObject: IfcMetaObject): string {
  return metaObject.name || metaObject.type || String(metaObject.id);
}

export const bracedName = (label: string) => `{${label}}`;

export function compareNames(a: string, b: string): number {
  const aNum = Number(a);
  const bNum = Number(b);
  if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
  return a.localeCompare(b);
}

export const compareNodes = (a: ExplorerTreeNode, b: ExplorerTreeNode) => {
  if (a.elevation !== undefined && b.elevation !== undefined) {
    if (a.elevation !== b.elevation) return b.elevation - a.elevation;
  } else if (a.elevation !== undefined) {
    return -1;
  } else if (b.elevation !== undefined) {
    return 1;
  }
  return compareNames(a.name, b.name);
};

export const sortNodes = (nodes: ExplorerTreeNode[]): ExplorerTreeNode[] =>
  nodes.sort(compareNodes);

export function createNode(
  key: string,
  name: string,
  objectId: string | undefined,
  children: ExplorerTreeNode[],
  title?: string,
  view?: ExplorerViewPoint,
): ExplorerTreeNode {
  if (objectId && children.length > 0) {
    children = [createNode(`${key}-mesh`, name, objectId, [], title), ...children];
    objectId = undefined;
  }

  const objectIds = children.flatMap((child) => child.objectIds);
  if (objectId) objectIds.push(objectId);

  return { key, name, search: name.toLowerCase(), title, objectId, objectIds, view, children };
}

export function specialNode(
  key: string,
  label: string,
  children: ExplorerTreeNode[],
): ExplorerTreeNode {
  const node = createNode(key, bracedName(label), undefined, children);
  node.special = true;
  return node;
}

export function classNodesFromMap(
  nodesByType: Record<string, ExplorerTreeNode[]>,
): ExplorerTreeNode[] {
  return Object.keys(nodesByType)
    .sort(compareNames)
    .map((type) => createNode(`class-${type}`, type, undefined, sortNodes(nodesByType[type])));
}
